import React, { useRef, useState, useEffect } from "react";
import ResizableHandler from "./ResizableHandler";
import RotatableHandler from "./RotatableHandler";
import Toolbar from "./Toolbar.js";

const EditableElement = ({
  element,
  isActive,
  onSetActive,
  onUpdate,
  onRemove,
}) => {
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const elementRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: element.width || 0,
    height: element.height || 0,
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [rotation, setRotation] = useState(element.rotation || 0);
  const [text, setText] = useState(element.text || "Editable Text");
  const [fontSize, setFontSize] = useState(element.fontSize || 16);
  const [color, setColor] = useState(element.color || "#000000");
  const [fontFamily, setFontFamily] = useState(element.fontFamily || "Arial");
  const [backgroundColor, setBackgroundColor] = useState(
    element.backgroundColor || "none"
  );
  const [fontWeight, setFontWeight] = useState(element.fontWeight || "normal");

  useEffect(() => {
    if (element.isEditing && elementRef.current) {
      elementRef.current.focus();
    }
  }, [element.isEditing]);

  useEffect(() => {
    if (
      elementRef.current &&
      !isInitialized &&
      element.type === "image" &&
      element.id !== "image-staff"
    ) {
      const naturalWidth = elementRef.current.naturalWidth;
      const naturalHeight = elementRef.current.naturalHeight;
      const aspectRatio = naturalWidth / naturalHeight;

      let width = 250 * aspectRatio;
      let height = 250;

      if (width > 678) {
        width = 678;
        height = width / aspectRatio;
      }
      if (height > 500) {
        height = 500;
        width = height * aspectRatio;
      }

      const cardRect = document
        .getElementById("birthday-card")
        .getBoundingClientRect();
      const positionX = (cardRect.width - width) / 2;
      const positionY = (cardRect.height - height) / 2;

      setDimensions({ width, height });
      onUpdate({
        ...element,
        width,
        height,
        position: { x: positionX, y: positionY },
        rotation: 0,
      });
      setIsInitialized(true);
    }
  }, [elementRef, element, onUpdate, isInitialized]);

  const handleMouseDown = (e) => {
    e.stopPropagation();
    if (e.target.closest(".tool-icon")) return;

    const rect = e.target.getBoundingClientRect();
    dragOffsetRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    setIsDragging(true);

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    const cardRect = document
      .getElementById("birthday-card")
      .getBoundingClientRect();
    const newX = e.clientX - cardRect.left - dragOffsetRef.current.x;
    const newY = e.clientY - cardRect.top - dragOffsetRef.current.y;

    onUpdate({ ...element, position: { x: newX, y: newY } });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handleResize = ({ width, height, x, y }) => {
    const widthFactor = width / dimensions.width;
    const heightFactor = height / dimensions.height;

    // Use the smaller factor to prevent excessive growth
    const scaleFactor = Math.min(widthFactor, heightFactor);

    // Limit font size scaling to prevent overlapping
    const newFontSize = Math.max(Math.floor(fontSize * scaleFactor), 10); // Use Math.floor() to round down

    setDimensions({ width, height });
    setFontSize(newFontSize);

    onUpdate({
      ...element,
      width,
      height,
      fontSize: newFontSize,
      position: { x, y },
    });
  };

  const handleRotate = (newRotation) => {
    setRotation(newRotation);
    onUpdate({ ...element, rotation: newRotation });
  };

  const handleTextChange = (e) => {
    // Ensure we have a valid selection and range
    const selection = window.getSelection();
    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    let cursorOffset = null;
    let cursorNode = null;

    if (range) {
      cursorOffset = range.startOffset;
      cursorNode = range.startContainer;
    }

    // Directly update the DOM (prevents re-render)
    setTimeout(() => {
      adjustHeight(); // Auto-resize height

      if (cursorNode && cursorOffset !== null) {
        const newSelection = window.getSelection();
        const newRange = document.createRange();

        if (
          document.body.contains(cursorNode) &&
          cursorNode.textContent.length >= cursorOffset
        ) {
          newRange.setStart(cursorNode, cursorOffset);
          newRange.collapse(true);
          newSelection.removeAllRanges();
          newSelection.addRange(newRange);
        }
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent default Enter behavior

      const selection = window.getSelection();
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

      if (!range) return; // Prevent error if range is invalid

      range.collapse(false); // Ensure cursor stays in the correct place

      const br = document.createElement("br");
      range.insertNode(br);

      // Add an extra <br> if needed to make sure the new line registers correctly
      const extraBr = document.createElement("br");
      range.insertNode(extraBr);

      // Move the cursor after the second <br>
      range.setStartAfter(extraBr);
      range.collapse(true);

      selection.removeAllRanges();
      selection.addRange(range);

      // Cleanup extra <br> that might be created
      setTimeout(() => {
        const allBrs = elementRef.current.querySelectorAll("br");
        if (
          allBrs.length > 1 &&
          allBrs[allBrs.length - 1].nextSibling === null
        ) {
          allBrs[allBrs.length - 1].remove();
        }
      }, 0);
    }
  };

  const handleTextBlur = () => {
    onUpdate({ ...element, text });
  };

  const handleClick = (e) => {
    e.stopPropagation(); // Prevent parent click
    onSetActive(element.id);
  };

  const adjustHeight = () => {
    if (elementRef.current && elementRef.current.parentElement) {
      const parent = elementRef.current.parentElement;
      const contentHeight = elementRef.current.scrollHeight; // Get the actual content height

      // Ensure parent only updates height if it's smaller than `dimensions.height`
      if (contentHeight > dimensions.height) {
        parent.style.height = "auto"; // Reset height first to shrink if needed
        parent.style.height = `${contentHeight}px`; // Adjust to content height
      }
    }
  };

  const handleShapeChange = (shape) => {
    onUpdate({ ...element, shape });
  };

  const defaultImageIsError =
    "https://storage.googleapis.com/exo24-intra-bucket/photo/birthday/template/user-default-avatar.jpg";

  return (
    <div
      style={{
        position: "absolute",
        left: `${element.position.x}px`,
        top: `${element.position.y}px`,
        zIndex: isHovered || isActive ? 2 : 1,
        transform: `rotate(${rotation}deg)`,
        transformOrigin: "center",
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        border: isHovered || isActive ? "2px solid #21ba45" : "none",
        userSelect: "none",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      {element.type === "image" ? (
        <img
          ref={elementRef}
          src={element.src || defaultImageIsError}
          alt="Draggable"
          draggable="false"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = defaultImageIsError;
          }}
          style={{
            width: "100%",
            height: "100%",
            // objectFit: "fill",
            objectFit: "cover",
            borderRadius: element.shape === "circle" ? "50%" : "0%", // Apply shape
            cursor: isDragging ? "grabbing" : "move",
          }}
        />
      ) : (
        <div
          ref={elementRef}
          contentEditable
          suppressContentEditableWarning={true}
          onInput={handleTextChange}
          onBlur={handleTextBlur}
          onKeyDown={handleKeyDown}
          style={{
            width: "100%",
            height: "100%", // Let height expand automatically
            fontFamily: `${fontFamily}`,
            fontSize: `${fontSize}px`,
            lineHeight: `${fontSize + 5}px`,
            textAlign: "center",
            color: color,
            fontWeight: `${fontWeight}`,
            background: `${backgroundColor}`,
            cursor: isDragging ? "grabbing" : "default",
            wordBreak: "break-word", // Ensures proper wrapping
            overflow: "hidden",
            whiteSpace: "pre-wrap", // Allows multi-line text
            boxSizing: "border-box",
            border: "none",
            outline: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column", // Ensures text stays inside
            borderRadius: "4px",
            opacity: 0.9,
          }}
        >
          {text}
        </div>
      )}

      <ResizableHandler
        onResize={handleResize}
        isActive={isActive}
        dimensions={dimensions}
        position={element.position}
        rotation={rotation}
      />

      <RotatableHandler
        onRotate={handleRotate}
        isActive={isActive}
        position={element.position}
        dimensions={dimensions}
      />

      {isActive && !isDragging && (
        <Toolbar
          type={element.type}
          fontSize={fontSize}
          setFontSize={(size) => {
            setFontSize(size);
            onUpdate({ ...element, fontSize: size });
          }}
          color={color}
          setColor={(newColor) => {
            setColor(newColor);
            onUpdate({ ...element, color: newColor });
          }}
          fontFamily={fontFamily}
          setFontFamily={(newFontFamily) => {
            setFontFamily(newFontFamily);
            onUpdate({ ...element, fontFamily: newFontFamily });
          }}
          fontWeight={fontWeight}
          setFontWeight={(newFontWeight) => {
            setFontWeight(newFontWeight);
            onUpdate({ ...element, fontWeight: newFontWeight });
          }}
          backgroundColor={backgroundColor}
          setBackgroundColor={(newBackgroundColor) => {
            setBackgroundColor(newBackgroundColor);
            onUpdate({ ...element, backgroundColor: newBackgroundColor });
          }}
          shape={element.shape}
          setShape={handleShapeChange}
          onRemove={() => onRemove(element.id)}
        />
      )}
    </div>
  );
};

export default EditableElement;
